import { atom } from 'jotai';
import type { StreamStatusEvent, AgentActivity, Citation, TokenUsage, IMessage } from '@/types';
import { EMessageRole } from '@/types';

/**
 * Streaming State
 *
 * ALL streaming data lives here during an active stream.
 * When stream_end arrives, the final message is committed
 * to TanStack Query cache, and this state is reset.
 */
interface StreamingState {
	isStreaming: boolean;
	conversationId: string | null;
	status: StreamStatusEvent | null;
	activities: AgentActivity[];
	citations: Citation[];
	messageText: string;
	thinkingText: string;
	tokenUsage: TokenUsage | null;
	suggestions: string[];
}

const initialState: StreamingState = {
	isStreaming: false,
	conversationId: null,
	status: null,
	activities: [],
	citations: [],
	messageText: '',
	thinkingText: '',
	tokenUsage: null,
	suggestions: []
};

const streamingStateBaseAtom = atom<StreamingState>(initialState);

// ===== Read-only Selectors =====

/** Is streaming currently active? */
export const isStreamingAtom = atom((get) => get(streamingStateBaseAtom).isStreaming);

/** Conversation ID being streamed to */
export const streamingConversationIdAtom = atom((get) => get(streamingStateBaseAtom).conversationId);

/** Current status (for StatusIndicator component) */
export const streamingStatusAtom = atom((get) => get(streamingStateBaseAtom).status);

/** Agent activities for ActivitiesTab */
export const streamingActivitiesAtom = atom((get) => get(streamingStateBaseAtom).activities);

/** Citations for CitationsTab */
export const streamingCitationsAtom = atom((get) => get(streamingStateBaseAtom).citations);

/** Current streaming message text */
export const streamingMessageTextAtom = atom((get) => get(streamingStateBaseAtom).messageText);

/** Current model thinking/reasoning text */
export const streamingThinkingTextAtom = atom((get) => get(streamingStateBaseAtom).thinkingText);

/** Token usage for MetaTab */
export const streamingTokenUsageAtom = atom((get) => get(streamingStateBaseAtom).tokenUsage);

/**
 * Get complete streaming message for display
 * Returns null when not streaming
 *
 * Components can use this to append to cached messages:
 * ```
 * const streamingMessage = useAtomValue(streamingMessageAtom);
 * const allMessages = streamingMessage
 *   ? [...cachedMessages, streamingMessage]
 *   : cachedMessages;
 * ```
 */
/**
 * Strip raw <<cite:...>> markers for streaming display.
 * During streaming the model emits inline citation paths that look ugly to the user.
 * The content_replace event will swap in the final text with [n] references.
 */
function cleanStreamingMarkers(text: string): string {
	return (
		text
			// Remove complete <<cite:...>> markers
			.replace(/<<cite:[^>]*>>/g, '')
			// Remove trailing incomplete marker (chunk boundary)
			.replace(/<<cite:[^>]*$/g, '')
			// Remove complete <<suggest:...>> markers
			.replace(/<<suggest:[^>]*>>/g, '')
			// Remove trailing incomplete suggestion marker
			.replace(/<<suggest:[^>]*$/g, '')
	);
}

export const streamingMessageAtom = atom((get): IMessage | null => {
	const state = get(streamingStateBaseAtom);
	if (!state.isStreaming || !state.conversationId) return null;

	return {
		id: 'streaming',
		conversationId: state.conversationId,
		text: cleanStreamingMarkers(state.messageText),
		role: EMessageRole.ASSISTANT,
		createdAt: new Date(),
		updatedAt: new Date(),
		agentActivities: state.activities,
		citations: state.citations,
		tokenUsage: state.tokenUsage,
		suggestions: state.suggestions
	};
});

/** Capture current streaming state snapshot (for stop generation) */
export const captureStreamingStateAtom = atom((get) => {
	const state = get(streamingStateBaseAtom);
	return {
		conversationId: state.conversationId,
		messageText: state.messageText,
		activities: state.activities,
		citations: state.citations,
		tokenUsage: state.tokenUsage
	};
});

// ===== Write Actions =====

/** Start a new streaming session */
export const startStreamingAtom = atom(null, (_get, set, conversationId: string) => {
	set(streamingStateBaseAtom, { ...initialState, isStreaming: true, conversationId });
});

/** Append text chunk to the streaming message */
export const appendContentAtom = atom(null, (_get, set, chunk: string) => {
	set(streamingStateBaseAtom, (prev) => ({
		...prev,
		messageText: prev.messageText + chunk
	}));
});

/** Replace full streaming message text (used to swap citation markers after streaming) */
export const replaceContentAtom = atom(null, (_get, set, text: string) => {
	set(streamingStateBaseAtom, (prev) => ({
		...prev,
		messageText: text
	}));
});

/** Update current status */
export const setStreamingStatusAtom = atom(null, (_get, set, status: StreamStatusEvent | null) => {
	set(streamingStateBaseAtom, (prev) => ({ ...prev, status }));
});

/** Add an agent activity */
export const addActivityAtom = atom(null, (_get, set, activity: AgentActivity) => {
	set(streamingStateBaseAtom, (prev) => ({
		...prev,
		activities: [...prev.activities, activity]
	}));
});

/** Append a thinking/reasoning chunk */
export const appendThinkingAtom = atom(null, (_get, set, chunk: string) => {
	set(streamingStateBaseAtom, (prev) => ({
		...prev,
		thinkingText: prev.thinkingText + chunk
	}));
});

/** Set citations from evidence event */
export const setCitationsAtom = atom(null, (_get, set, citations: Citation[]) => {
	set(streamingStateBaseAtom, (prev) => ({ ...prev, citations }));
});

/** Set token usage */
export const setTokenUsageAtom = atom(null, (_get, set, tokenUsage: TokenUsage) => {
	set(streamingStateBaseAtom, (prev) => ({ ...prev, tokenUsage }));
});

/** Set follow-up suggestions */
export const setSuggestionsAtom = atom(null, (_get, set, suggestions: string[]) => {
	set(streamingStateBaseAtom, (prev) => ({ ...prev, suggestions }));
});

/** End streaming and reset state */
export const endStreamingAtom = atom(null, (_get, set) => {
	set(streamingStateBaseAtom, initialState);
});

// Export base for devtools
export const streamingStateAtom = streamingStateBaseAtom;
